import { Link } from "react-router-dom";
import { Sun, Package, Shield, Building2, ArrowRight, CheckCircle } from "lucide-react";

const features = [
  "Solar powered system (no external electricity required)",
  "Automatic plastic waste compression using inbuilt compressor",
  "Sensor-based operation (touchless usage)",
  "120L capacity (high efficiency)",
  "Segregation-friendly design (Plastic, Glass, Cans, Paper)",
  "Durable metal body for outdoor environments",
  "Suitable for urban, semi-urban, and institutional deployment",
];

const deploymentTargets = [
  "CSR initiatives by companies",
  "Municipal Corporations (Nagar Nigam)",
  "Nagar Palika / Panchayats",
  "Smart City Projects",
  "Public infrastructure & urban spaces",
  "Corporate campuses & institutions",
];

export default function ProductPage() {
  return (
    <div className="pt-20" data-testid="product-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
                <Package className="h-4 w-4" /> Products
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
                Solar Powered Smart Dustbin
              </h1>
              <p className="mt-4 text-lg text-emerald-300 font-medium">
                Plastic Waste Compression Technology
              </p>
              <p className="mt-6 text-lg text-slate-400 leading-relaxed">
                A smart, solar-powered dustbin with 120-liter capacity designed for efficient plastic waste management. Uses sensor-based automatic compression technology to reduce volume and increase storage capacity.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact" className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-all">
                  Request Bulk Proposal
                </Link>
                <a href="https://wa.me/918575900009?text=Hi%2C%20I%27m%20interested%20in%20the%20Solar%20Smart%20Dustbin%20for%20bulk%20deployment." target="_blank" rel="noopener noreferrer" className="px-6 py-3 border border-green-500/50 text-green-400 hover:bg-green-500/10 font-semibold rounded-lg transition-all">
                  WhatsApp for Inquiry
                </a>
              </div>
            </div>
            <div className="relative">
              <img src="/images/smart-dustbin.jpg" alt="Solar Powered Smart Dustbin by The Carbon Meridian" className="rounded-2xl shadow-2xl w-full" data-testid="product-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Sun className="h-6 w-6 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900">Core Features</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-5 rounded-xl bg-emerald-50 border border-emerald-100">
                <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                <span className="text-slate-700 font-medium">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bulk Deployment */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-bold mb-6">
                BULK ORDERS ONLY
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Bulk Deployment & CSR Partnerships</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                This is a bulk deployment product, NOT retail. Organizations can deploy these smart dustbins under:
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-700">
                  <Shield className="h-5 w-5 text-amber-500 shrink-0" />
                  Corporate Social Responsibility (CSR)
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <Building2 className="h-5 w-5 text-amber-500 shrink-0" />
                  Government tenders
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-amber-500 shrink-0" />
                  Urban cleanliness drives
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <Sun className="h-5 w-5 text-amber-500 shrink-0" />
                  Sustainability initiatives
                </li>
              </ul>
            </div>
            <div>
              <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Suitable For</h3>
                <div className="space-y-3">
                  {deploymentTargets.map((target, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                      <span className="text-slate-700">{target}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Partner With Us for Deployment</h2>
          <p className="text-emerald-100 text-lg mb-8">Get custom bulk pricing and deployment plans for your organization or municipality.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="px-8 py-3 bg-white text-emerald-700 font-bold rounded-lg hover:bg-emerald-50 transition-all flex items-center gap-2">
              Request Bulk Proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="https://wa.me/918575900009?text=Hi%2C%20I%27m%20interested%20in%20bulk%20deployment%20of%20Smart%20Dustbins." target="_blank" rel="noopener noreferrer" className="px-8 py-3 border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-all">
              WhatsApp for Partnership
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
